import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import SectionHeading from './SectionHeading';
import { useInView, reveal } from './motion';
import { useT } from '../../i18n/LanguageContext';

// The concrete payoff of buying the book — sits right after MethodBook on the
// landing. Each item links, in spirit, to a section of the /piattaforma page.
const getItems = (t) => [
  {
    Icon: AutoAwesomeOutlinedIcon,
    title: t('home.whatYouGet.item1Title'),
    body: t('home.whatYouGet.item1Body'),
  },
  {
    Icon: DownloadOutlinedIcon,
    title: t('home.whatYouGet.item2Title'),
    body: t('home.whatYouGet.item2Body'),
  },
  {
    Icon: StyleOutlinedIcon,
    title: t('home.whatYouGet.item3Title'),
    body: t('home.whatYouGet.item3Body'),
  },
  {
    Icon: GroupsOutlinedIcon,
    title: t('home.whatYouGet.item4Title'),
    body: t('home.whatYouGet.item4Body'),
  },
  {
    Icon: NoteAddOutlinedIcon,
    title: t('home.whatYouGet.item5Title'),
    body: t('home.whatYouGet.item5Body'),
  },
  {
    Icon: DesignServicesOutlinedIcon,
    title: t('home.whatYouGet.item6Title'),
    body: t('home.whatYouGet.item6Body'),
  },
];

function Item({ item, i }) {
  const theme = useTheme();
  const { ref, visible } = useInView();
  const { Icon } = item;
  return (
    <Box ref={ref} sx={{ ...reveal(visible, (i % 3) * 0.08) }}>
      <Box sx={{
        width: 52, height: 52, borderRadius: '14px', display: 'grid', placeItems: 'center',
        bgcolor: '#fff', color: theme.home.cyanText, mb: 2.5,
        boxShadow: theme.home.softShadow || '0 6px 18px -10px rgba(20,24,29,0.28)',
      }}>
        <Icon fontSize="medium" />
      </Box>
      <Typography variant="h3" sx={{ fontSize: { xs: '1.25rem', md: '1.4rem' }, color: theme.home.ink, mb: 1.25 }}>
        {item.title}
      </Typography>
      <Typography sx={{ color: theme.home.inkMuted, lineHeight: 1.65, fontSize: '1.05rem' }}>
        {item.body}
      </Typography>
    </Box>
  );
}
// ponytail: body copy at 1.05rem to match PrivacyOwnership / HowItWorks siblings.

export default function WhatYouGet() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { ref, visible } = useInView();
  const t = useT();
  const items = getItems(t);

  return (
    <Box id="cosa-ottieni" sx={{ bgcolor: theme.home.paper, py: theme.home.sectionPy }}>
      <Box sx={{ maxWidth: theme.home.maxWidth, mx: 'auto', px: { xs: 3, md: 5 } }}>
        <SectionHeading
          eyebrow={t('home.whatYouGet.eyebrow')}
          accent={theme.home.coralText}
          title={t('home.whatYouGet.title')}
          lede={t('home.whatYouGet.lede')}
        />
        <Box sx={{
          mt: { xs: 5, md: 8 }, display: 'grid', gap: { xs: 5, md: 6 },
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        }}>
          {items.map((it, i) => <Item key={it.title} item={it} i={i} />)}
        </Box>

        <Stack
          ref={ref}
          direction={{ xs: 'column', sm: 'row' }} spacing={2}
          sx={{ mt: { xs: 6, md: 8 }, ...reveal(visible) }}
        >
          <Button
            variant="contained" size="large" endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/piattaforma')}
            sx={{ px: 3.5, py: 1.4, borderRadius: 999, bgcolor: theme.home.ink,
              '&:hover': { bgcolor: theme.home.inkHover }, boxShadow: theme.home.cardShadow }}
          >
            {t('home.whatYouGet.ctaPlatform')}
          </Button>
          <Button
            variant="outlined" size="large" onClick={() => navigate('/redeem')}
            sx={{ px: 3.5, py: 1.4, borderRadius: 999, borderColor: theme.home.ink, color: theme.home.ink,
              '&:hover': { borderColor: theme.home.ink, bgcolor: 'rgba(20,24,29,0.04)' } }}
          >
            {t('home.whatYouGet.ctaRedeem')}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
